import moment from 'moment'
import React from 'react'
import { Popup } from 'react-leaflet'
import styled, { css } from 'styled-components'
import { MapWidgetEvent } from './api'
import { Scrollbars } from 'react-custom-scrollbars'
import _ from 'lodash'

export default function Tooltip({
    event,
    onClose
}: {
    event: MapWidgetEvent
    onClose: (cleanSearch?: boolean) => void
}): React.ReactElement {
    return (
        <Popup closeButton={false}>
            <button className="closeButton" onClick={(): void => onClose(true)}>
                X
            </button>
            <p className="leaflet-popup__assetName">
                {event.asset}{' '}
                {_.has(event, 'tooltip.link') ? (
                    <a className="externalLink" target="blank" href={event.tooltip.link}>
                        <i className="fas fa-external-link" />
                    </a>
                ) : null}
            </p>
            <div className="leaflet-popup__subtitle">
                <p>{_.get(event, 'tooltip.caption', null)}</p>
                <p>
                    {event.bbox ? (
                        <span>Bounding box: {event.bbox.join(', ')}</span>
                    ) : (
                        <>
                            LAT/LONG: {event.lat}, {event.long}
                        </>
                    )}
                </p>
            </div>
            <CustomScrollbars maxHeight={78}>
                {_.has(event, 'tooltip.info') &&
                    Object.keys(event.tooltip.info).map(el => {
                        return (
                            <p key={el} className={`leaflet-popup__tooltip`}>
                                <i className="fa fa-chevron-right" /> <strong>{el.toUpperCase()}: &nbsp; </strong>
                                {event.tooltip.info[el]}
                            </p>
                        )
                    })}
            </CustomScrollbars>
            <div className="leaflet-popup__footer">
                <p className="leaflet-popup__update">
                    <i className="fal fa-clock" />
                    LAST UPDATE: {moment(event.timestamp).format('llll')}
                </p>
            </div>
        </Popup>
    )
}

const CustomScrollbars = ({
    maxHeight,
    ...props
}: {
    maxHeight: number
    children: React.ReactNode
}): React.ReactElement => {
    const renderThumb = ({ style }: { style: React.CSSProperties }): React.ReactElement => {
        const thumbStyle = {
            backgroundColor: '#626b80',
            width: '6px',
            height: '22px'
        }

        return <div style={{ ...style, ...thumbStyle }} />
    }

    const renderTrack = ({ style }: { style: React.CSSProperties }): React.ReactElement => {
        const trackStyle = {
            top: 0,
            right: 0,
            backgroundColor: '#434957',
            width: '6px',
            height: '100%'
        }

        return <div style={{ ...style, ...trackStyle }} />
    }

    const renderView = ({ style }: { style: React.CSSProperties }): React.ReactElement => {
        const renderStyle = {
            maxHeight: maxHeight
        }

        return <div style={{ ...style, ...renderStyle }} />
    }

    const containerProps = {
        marginBottom: '5px'
    }

    return (
        <Scrollbars
            renderTrackVertical={renderTrack}
            renderThumbVertical={renderThumb}
            renderView={renderView}
            autoHeight
            hideTracksWhenNotNeeded={true}
            style={{ ...containerProps }}
            {...props}
        />
    )
}

export const TooltipStyles = theme => css`
    .leaflet-popup {
        &-content-wrapper {
            width: 245px;
            height: auto;
            opacity: 0.85;
            border-radius: 5px;
            box-shadow: 0 1px 8px 0 rgba(54, 65, 89, 0.15);
            background-color: ${theme == 'light' ? '#0d1526' : '#fff'};
            position: relative;
            .closeButton {
                position: absolute;
                color: #37a3ef;
                right: 10px;
                top: 10px;
                background: none;
                border: none;
                font-weight: bold;
            }
            .externalLink {
                margin-left: 10px;
            }
        }
        &-tip-container {
            position: absolute;
            left: 50%;
            margin-left: -20px;
        }
        &-tip {
            opacity: 0.85;
            background-color: ${theme == 'light' ? '#0d1526' : '#fff'};
            border-radius: 4px;
        }
        p {
            margin-top: 0;
            display: flex;
            align-items: baseline;
        }

        &__footer {
            p {
                margin: 0 !important;
            }
            i {
                width: 9px;
            }
        }

        &__assetName {
            font-size: 14px;
            font-weight: 600;
            color: #37a3ef;
            margin: 0;
        }

        &__tooltip {
            font-size: 12px;
            color: ${theme == 'light' ? '#a9bbe3' : '#707c97'};
            margin-bottom: 5px;
            i {
                color: #37a3ef;
                margin: 0 9px;
            }
        }

        &__subtitle {
            flex-direction: column;
            margin-bottom: 8px;
            > p {
                font-size: 9px;
                color: ${theme == 'light' ? '#747882' : '#4d618b'};
                margin: 0;
            }
        }
        &__update {
            font-size: 9px;
            /* transform: scale(0.9); */
            color: ${theme == 'light' ? '#747882' : '#4d618b'};
            margin-top: 2px;

            i {
                color: ${theme == 'light' ? '#747882' : '#4d618b'};
                margin-right: 7px;
            }
        }
    }
`
